import { scaleLog } from '@visx/scale'
import { memo, useMemo } from 'react'

import { formatMs } from '../../lib/format.ts'
import type { LatencyMs } from '../../lib/sse/types.ts'
import { useElementSize } from './useElementSize.ts'

const HEIGHT = 46
const AXIS_Y = 30

/**
 * The three percentiles on one logarithmic axis.
 *
 * Three separate numbers hide the thing that matters: the *spread*. p50 21ms
 * next to p99 213ms is a healthy pipeline; p50 21ms next to p99 4.2s is a tail
 * that is timing out for somebody. Putting them on a shared log axis makes that
 * gap a physical distance instead of an arithmetic exercise.
 */
export const LatencySpine = memo(function LatencySpine({ latency }: { latency: LatencyMs | null }) {
  const [ref, size] = useElementSize<HTMLDivElement>()
  const width = size.width

  const { scale, ticks } = useMemo(() => {
    const upper = Math.max(100, (latency?.p99 ?? 100) * 1.6)
    const logScale = scaleLog<number>({
      domain: [1, upper],
      range: [0, Math.max(1, width - 8)],
      clamp: true,
    })
    const decades = [1, 10, 100, 1_000, 10_000].filter((tick) => tick <= upper)
    return { scale: logScale, ticks: decades }
  }, [latency, width])

  const markers =
    latency === null
      ? []
      : ([
          { key: 'p50', value: latency.p50, tone: 'var(--c-seq-4)' },
          { key: 'p95', value: latency.p95, tone: 'var(--c-seq-3)' },
          { key: 'p99', value: latency.p99, tone: 'var(--c-series-1)' },
        ] as const)

  const label =
    latency === null
      ? 'Latência indisponível.'
      : `Latência ponta a ponta: p50 ${formatMs(latency.p50)}, p95 ${formatMs(
          latency.p95,
        )}, p99 ${formatMs(latency.p99)}.`

  return (
    <div ref={ref} className="live-chart w-full">
      {width > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label={label}>
          <g transform="translate(4, 0)">
            <line
              x1={0}
              x2={width - 8}
              y1={AXIS_Y}
              y2={AXIS_Y}
              stroke="var(--c-border-subtle)"
              strokeWidth={1}
              shapeRendering="crispEdges"
            />

            {ticks.map((tick) => (
              <g key={tick} transform={`translate(${scale(tick)}, 0)`}>
                <line
                  y1={AXIS_Y}
                  y2={AXIS_Y + 3}
                  stroke="var(--c-border-subtle)"
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                <text
                  y={HEIGHT - 2}
                  textAnchor="middle"
                  fontFamily="var(--font-mono)"
                  fontSize={10}
                  fill="var(--c-axis)"
                >
                  {tick >= 1000 ? `${tick / 1000}s` : `${tick}ms`}
                </text>
              </g>
            ))}

            {markers.map((marker) => (
              <g key={marker.key} transform={`translate(${scale(Math.max(1, marker.value))}, 0)`}>
                <line
                  y1={10}
                  y2={AXIS_Y}
                  stroke={marker.tone}
                  strokeWidth={2}
                  shapeRendering="crispEdges"
                />
                <text
                  y={7}
                  textAnchor="middle"
                  fontFamily="var(--font-mono)"
                  fontSize={10}
                  fill="var(--c-text-secondary)"
                >
                  {marker.key}
                </text>
              </g>
            ))}
          </g>
        </svg>
      )}
    </div>
  )
})
